import { useEffect, useState } from "react";

import { Switch, Grid, CircularProgress, useTheme } from "@mui/material";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import Modal from "@mui/material/Modal";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { request } from "@/Api";
import { selectPersonId } from "@/auth/authSlice";
import { PERMISSION_CREATE_GLOBAL_BID_TEMPLATES } from "@/auth/constants";
import useEntitlements from "@/auth/useEntitlements";
import { CustomBox } from "@/common/StyledComponents";
import { BID_STATUS_DRAFT } from "@/sheet/constants";

export default function BidTemplatePopup({
    bidTemplatePopupOpen,
    setBidTemplatePopupOpen,
    bidId,
    isPublic,
}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const [templateCategories, setTemplateCategories] = useState([]);
    const [templateName, setTemplateName] = useState("");
    const [templateCategory, setTemplateCategory] = useState("");
    const [templateShared, setTemplateShared] = useState(false);
    const [showTemplateSaved, setShowTemplateSaved] = useState(false);
    const [creatingTemplate, setCreatingTemplate] = useState(false);

    const personId = useSelector(selectPersonId);
    const { hasPermission } = useEntitlements();
    const canCreateGlobalTemplates = hasPermission(
        PERMISSION_CREATE_GLOBAL_BID_TEMPLATES,
    );

    const loadCategories = () => {
        request
            .get("/bid-templates/categories", {
                params: { shared: templateShared },
            })
            .then((response) => {
                setTemplateCategories(response.data);
            });
    };

    const createTemplateBid = () => {
        request
            .post("/bids", {
                name: templateName,
                project_id: null,
                is_change_order: false,
                status: BID_STATUS_DRAFT,
                bid_type: "Template",
            })
            .then((response) => {
                createTemplate(response.data.id);
            });
    };

    const copyTemplateBid = () => {
        setCreatingTemplate(true);
        request
            .post("/bids/copy", {
                bid_name: templateName,
                project_id: null,
                bid_id: bidId,
                bid_type: "Template",
                is_change_order: false,
                as_template: true,
                public: isPublic,
            })
            .then((response) => {
                createTemplate(response.data.id);
            });
    };

    const createTemplate = (bid_id) => {
        request
            .post("/bid-templates", {
                category: templateCategory || null,
                bid_id: bid_id,
                person_id:
                    templateShared && canCreateGlobalTemplates
                        ? null
                        : personId,
            })
            .then((response) => {
                setBidTemplatePopupOpen(false);
                setShowTemplateSaved(true);
                setCreatingTemplate(false);
                navigate("/bids/" + response.data.bid_id);
            });
    };

    const handleSave = () => {
        if (bidId) {
            copyTemplateBid();
        } else {
            createTemplateBid();
        }
    };

    useEffect(loadCategories, [templateShared]);

    return (
        <>
            <Modal
                sx={{ overflow: "scroll" }}
                open={bidTemplatePopupOpen}
                onClose={() => {
                    setBidTemplatePopupOpen(false);
                }}
            >
                <CustomBox
                    overflow="scroll"
                    sx={{ background: theme.palette.background.default }}
                >
                    <Grid container flexDirection="column" gap={3}>
                        <Grid item>
                            <h1>{bidId ? `Copy` : `Create`} Template</h1>
                        </Grid>

                        <Grid item>
                            <InputLabel>Name</InputLabel>
                            <TextField
                                fullWidth
                                value={templateName}
                                onChange={(e) => {
                                    setTemplateName(e.target.value);
                                }}
                            />
                        </Grid>

                        <Grid item>
                            <InputLabel>Category</InputLabel>
                            <Autocomplete
                                freeSolo
                                value={templateCategory}
                                options={templateCategories}
                                onInputChange={(e, value) => {
                                    setTemplateCategory(value);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} />
                                )}
                            />
                        </Grid>

                        {canCreateGlobalTemplates && (
                            <Grid item>
                                <InputLabel>Shared</InputLabel>
                                <Switch
                                    checked={templateShared}
                                    onChange={() =>
                                        setTemplateShared(!templateShared)
                                    }
                                />
                            </Grid>
                        )}

                        <Grid item>
                            <Grid
                                container
                                sx={{ marginTop: "24px" }}
                                alignItems="center"
                                justifyContent="flex-end"
                                spacing={2}
                            >
                                <Grid item>
                                    <Button
                                        color="secondary"
                                        sx={{ marginRight: "6px" }}
                                        onClick={() =>
                                            setBidTemplatePopupOpen(false)
                                        }
                                    >
                                        Cancel
                                    </Button>
                                </Grid>
                                <Grid item>
                                    {creatingTemplate ? (
                                        <CircularProgress />
                                    ) : (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSave}
                                        >
                                            Save
                                        </Button>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </CustomBox>
            </Modal>
            <Snackbar
                open={showTemplateSaved}
                autoHideDuration={2000}
                onClose={(event, reason) => {
                    setShowTemplateSaved(false);
                }}
            >
                <Alert severity="success">Template Created!</Alert>
            </Snackbar>
        </>
    );
}
